import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { invokeEdgeFunction } from '@/utils/invokeEdgeFunction';

interface GenerateQuestionAIDialogProps {
  open: boolean;
  onClose: () => void;
  questionType: 'mcq' | 'open_ended';
  group?: { name: string; description?: string | null };
  onGenerated: (data: any) => void;
}

export function GenerateQuestionAIDialog({ open, onClose, questionType: initialType, group, onGenerated }: GenerateQuestionAIDialogProps) {
  const { toast } = useToast();
  const [type, setType] = useState<'mcq' | 'open_ended'>(initialType);
  const [difficulty, setDifficulty] = useState('');
  const [extraPrompt, setExtraPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const data = await invokeEdgeFunction('generate-assessment-question-gemini', {
        body: {
          groupName: group?.name ?? null,
          groupDescription: group?.description ?? null,
          questionType: type,
          difficulty,
          extraPrompt,
        },
      });

      onGenerated(data);
      onClose();
    } catch (error) {
      console.error('AI generation failed', error);
      toast({ title: 'Error', description: 'Failed to generate question', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Question with AI</DialogTitle>
          <DialogDescription>
            Provide a few details and let AI craft a question for you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <label className="text-sm font-medium">Question Type</label>
            <Select value={type} onValueChange={v => setType(v as 'mcq' | 'open_ended')}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mcq">Multiple Choice</SelectItem>
                <SelectItem value="open_ended">Open Ended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Difficulty (optional)</label>
            <Input value={difficulty} onChange={e => setDifficulty(e.target.value)} placeholder="e.g. easy" />
          </div>
          <div>
            <label className="text-sm font-medium">Extra Prompt (optional)</label>
            <Textarea value={extraPrompt} onChange={e => setExtraPrompt(e.target.value)} rows={3} />
          </div>
          {group && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p><span className="font-medium">Group:</span> {group.name}</p>
              {group.description && <p>{group.description}</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
